/**
 * Client Quick Card
 * Mobile-first client overview with health, financials, activity.
 */

import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Building2, Mail, Phone, Hash, Globe, Link2, FileText, MessageSquare, Calendar, TrendingUp, TrendingDown, Minus, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtBytes = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

const HEALTH_COLORS: Record<string, string> = {
  excellent: 'text-green-400 border-green-500/30',
  good: 'text-blue-400 border-blue-500/30',
  fair: 'text-amber-400 border-amber-500/30',
  poor: 'text-red-400 border-red-500/30',
};

export default function ClientCardPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['client-card', clientId],
    queryFn: async () => { const r = await apiClient.get(`/manager/client/${clientId}`); return r.data.data; },
    enabled: !!clientId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#7c75f0]" />
      </div>
    );
  }

  if (!data) return null;

  const { client, health, stats, financials, recentChats, recentDocs } = data;
  const healthColor = HEALTH_COLORS[health.level] || HEALTH_COLORS.fair;

  return (
    <div className="px-5 py-4 max-w-lg mx-auto space-y-5 pb-24">
      {/* Back button */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Client Header */}
      <div className="flex items-start gap-4">
        <div className={cn('w-14 h-14 rounded-xl border-2 flex items-center justify-center shrink-0', healthColor)}>
          <span className="text-xl font-bold">{health.score}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold truncate">{client.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {client.industry && <span className="text-xs text-zinc-500 bg-white/[0.04] px-2 py-0.5 rounded">{client.industry}</span>}
            {client.xeroConnected && <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Xero</span>}
            <span className={cn('text-xs px-2 py-0.5 rounded capitalize', health.level === 'excellent' ? 'text-green-400 bg-green-500/10' : health.level === 'good' ? 'text-blue-400 bg-blue-500/10' : health.level === 'fair' ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10')}>{health.level}</span>
          </div>
        </div>
      </div>

      {/* Quick Action: Ask about this client */}
      <button
        onClick={() => navigate(`/pa/voice`)}
        className="w-full flex items-center gap-3 p-4 bg-[#5048e5]/10 border border-[#5048e5]/20 rounded-xl transition hover:bg-[#5048e5]/15"
      >
        <Mic className="h-5 w-5 text-[#7c75f0]" />
        <span className="text-sm">Ask about {client.name.split(' ')[0]}</span>
      </button>

      {/* Contact Info */}
      <div className="space-y-2">
        {client.abn && (
          <div className="flex items-center gap-3 text-sm">
            <Hash className="h-4 w-4 text-zinc-600 shrink-0" />
            <span className="text-zinc-400">ABN: {client.abn}</span>
          </div>
        )}
        {client.email && (
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-4 w-4 text-zinc-600 shrink-0" />
            <a href={`mailto:${client.email}`} className="text-[#7c75f0] hover:underline truncate">{client.email}</a>
          </div>
        )}
        {client.phone && (
          <div className="flex items-center gap-3 text-sm">
            <Phone className="h-4 w-4 text-zinc-600 shrink-0" />
            <a href={`tel:${client.phone}`} className="text-zinc-400">{client.phone}</a>
          </div>
        )}
        {client.xeroOrgName && (
          <div className="flex items-center gap-3 text-sm">
            <Link2 className="h-4 w-4 text-zinc-600 shrink-0" />
            <span className="text-zinc-400 truncate">{client.xeroOrgName}</span>
          </div>
        )}
      </div>

      {/* Financial Snapshot */}
      {financials && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">{financials.period} Financials</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-zinc-600">Revenue</p>
              <p className="text-lg font-bold text-green-400">{fmtCurrency(financials.revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">Expenses</p>
              <p className="text-lg font-bold text-red-400">{fmtCurrency(financials.expenses)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">Net Profit</p>
              <p className={cn('text-lg font-bold', financials.netProfit >= 0 ? 'text-green-400' : 'text-red-400')}>
                {financials.netProfit >= 0 ? '' : '-'}{fmtCurrency(financials.netProfit)}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">Profit Margin</p>
              <div className="flex items-center gap-1">
                {financials.profitMargin > 0 ? <TrendingUp className="h-4 w-4 text-green-400" /> : financials.profitMargin < 0 ? <TrendingDown className="h-4 w-4 text-red-400" /> : <Minus className="h-4 w-4 text-zinc-500" />}
                <p className={cn('text-lg font-bold', financials.profitMargin >= 0 ? 'text-green-400' : 'text-red-400')}>{financials.profitMargin}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
          <FileText className="h-4 w-4 text-[#7c75f0] mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.documents}</p>
          <p className="text-xs text-zinc-600">Docs</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
          <MessageSquare className="h-4 w-4 text-[#7c75f0] mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.conversations}</p>
          <p className="text-xs text-zinc-600">Chats</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center">
          <Calendar className="h-4 w-4 text-amber-400 mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.daysSinceContact ?? '-'}</p>
          <p className="text-xs text-zinc-600">Days ago</p>
        </div>
      </div>

      {/* Health Factors */}
      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Health Breakdown</h3>
        {health.factors.map((f: any) => (
          <div key={f.name} className="flex items-center gap-3">
            <span className="text-sm text-zinc-400 w-20">{f.name}</span>
            <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', f.score / f.max >= 0.7 ? 'bg-green-500' : f.score / f.max >= 0.4 ? 'bg-amber-500' : 'bg-red-500')}
                style={{ width: `${(f.score / f.max) * 100}%` }}
              />
            </div>
            <span className="text-xs text-zinc-600 w-10 text-right">{f.score}/{f.max}</span>
          </div>
        ))}
      </div>

      {/* Recent Chats */}
      {recentChats.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Recent Conversations</h3>
          {recentChats.map((c: any) => (
            <div key={c.id} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0">
              <MessageSquare className="h-4 w-4 text-zinc-600 shrink-0" />
              <p className="text-sm text-zinc-400 flex-1 truncate">{c.title || 'Chat'}</p>
              <span className="text-xs text-zinc-700 shrink-0">{new Date(c.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Documents */}
      {recentDocs.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Recent Documents</h3>
          {recentDocs.map((d: any) => (
            <div key={d.id} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0">
              <FileText className="h-4 w-4 text-zinc-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-400 truncate">{d.original_filename}</p>
                {d.document_category && <span className="text-xs text-zinc-600">{d.document_category}</span>}
              </div>
              <span className="text-xs text-zinc-700 shrink-0">{new Date(d.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
